import * as pdfjsLib from "pdfjs-dist";
import pdfWorker from "pdfjs-dist/build/pdf.worker.entry";
import { parsePhoneNumberFromString } from "libphonenumber-js";
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;
import filetypeinfo from "magic-bytes.js";

// Calculate aspect ratio from width and height (accepts size string or individual numbers)
export const getAspectRatio = (sizeOrWidth, height) => {
  let width;

  if (typeof sizeOrWidth === "string") {
    [width, height] = sizeOrWidth.split("x").map(Number);
  } else {
    width = sizeOrWidth;
  }

  const gcd = (a, b) => {
    while (b !== 0) {
      [a, b] = [b, a % b];
    }
    return a;
  };

  const divisor = gcd(width, height);

  const simplifiedWidth = width / divisor;
  const simplifiedHeight = height / divisor;

  // If numbers are too large after simplification,
  // use decimal format instead
  if (simplifiedWidth > 20 || simplifiedHeight > 20) {
    const decimalRatio = Number((width / height).toFixed(2));
    return `${decimalRatio}:1`;
  }

  return `${simplifiedWidth}:${simplifiedHeight}`;
};

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export const validateImageType = async (file) => {
  const buffer = await file.slice(0, 20).arrayBuffer();

  const uint8Array = new Uint8Array(buffer);

  const result = filetypeinfo(uint8Array);

  return result.some((type) =>
    ALLOWED_IMAGE_TYPES.includes(type.mime)
  );
};

// Validate that uploaded image size is greater than or equal to expected size
export const validateImageSize = (file, expectedWidth, expectedHeight) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        if (img.width >= expectedWidth && img.height >= expectedHeight) {
          resolve({
            valid: true,
            dimensions: { width: img.naturalWidth, height: img.naturalHeight }
          });
        } else {
          resolve({
            valid: false,
            message: `Image size too small. Expected at least ${expectedWidth}x${expectedHeight}px, but got ${img.width}x${img.height}px.`,
          });
        }
      };
      img.onerror = () => {
        resolve({ valid: false, message: "Failed to load image. Please ensure the file is a valid image." });
      };
      img.src = event.target.result;
    };
    reader.onerror = () => {
      resolve({ valid: false, message: "Failed to read the file." });
    };
    reader.readAsDataURL(file);
  });
};


export const copyArrayBuffer = (arrayBuffer) => {
  const copiedBuffer = new ArrayBuffer(arrayBuffer.byteLength);
  const view = new Uint8Array(copiedBuffer);
  view.set(new Uint8Array(arrayBuffer));
  return copiedBuffer;
};

export const renderPdf = async (pdfBinaryData) => {
  try {
    const pdfDataCopy = copyArrayBuffer(pdfBinaryData);
    const pdf = await pdfjsLib.getDocument({ data: pdfDataCopy }).promise;
    const pdfImages = [];
    for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
      const page = await pdf.getPage(pageNumber);
      const scale = 1.5;
      const viewport = page.getViewport({ scale });
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      await page.render({ canvasContext: context, viewport }).promise;

      // Using toBlob and URL.createObjectURL to avoid length limits and improve performance
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
      const imageUrl = URL.createObjectURL(blob);
      pdfImages.push(imageUrl);
    }

    return pdfImages;
  } catch (error) {
    throw error;
  }
};


export const constructAndReturnValidationErrorMessage = (
  data
) => {
  const messages = data?.message || [];
  const errorDetails = Array.isArray(messages)
    ? messages.map((m) => ` ${m}`).join("\n")
    : String(messages);

  return errorDetails;
};

export const validateForm = (organization, phone, email, userName, planName) => {
  const missingFields = [];

  if (organization === "") missingFields.push('Organization Name');
  if (userName === "") missingFields.push('User Name');
  if (email === "") missingFields.push('Email Address');
  if (phone.trim() === "" || phone.trim().length <= 3) missingFields.push('Mobile Phone');
  if (planName === "") missingFields.push('Subscription Plan');

  if (missingFields.length > 0) {
    const message = missingFields.length === 1
      ? `${missingFields[0]} is required.`
      : `The following fields are required:\n${missingFields.join(', ')}.`;

    return {
      isValid: false,
      errorMessage: [message],
    }
  }

  if (email && !validateUserEmail(email)) {
    return {
      isValid: false,
      errorMessage: "The email format is invalid.",
    };
  }

  if (phone && phone.trim().length > 3 && !validatePhoneNumber(phone)) {
    return {
      isValid: false,
      errorMessage:
        "Phone number is not valid. Please enter a valid number with a correct area code, including the country code (e.g. +1 for US).",
    };
  }

  return { isValid: true };
};

export const validateUserEmail = (email) => {
  if (!email) return false;

  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
  return emailRegex.test(email);
};

export const validatePhoneNumber = (phone) => {
  const phoneNumber = parsePhoneNumberFromString(phone);
  return phoneNumber?.isValid() ?? false;
};

export const formatPhoneNumber = (value) => {
  const digits = value.replace(/\D/g, "").slice(0, 10);
  const part1 = digits.slice(0, 3);
  const part2 = digits.slice(3, 6);
  const part3 = digits.slice(6, 10);
  let formatted = part1;
  if (part2) formatted += "-" + part2;
  if (part3) formatted += "-" + part3;
  return formatted;
};


// Only image MIME types - no extension checking at all
const ALLOWED_IMAGE_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
];


export const resizeImage = async (
  file,
  baseWidth,      // width designed for 300 DPI
  baseHeight,     // height designed for 300 DPI
  targetDPI,
  quality
) => {
  const targetWidth = Math.ceil(baseWidth * (targetDPI / 300));
  const targetHeight = Math.ceil(baseHeight * (targetDPI / 300));

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      const img = new Image();

      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        canvas.width = targetWidth;
        canvas.height = targetHeight;

        ctx.clearRect(0, 0, targetWidth, targetHeight);
        ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

        let mimeType = file.type;

        canvas.toBlob(
          (blob) => {
            if (!blob) return reject(new Error("Canvas is empty"));
            resolve(blob);
          },
          mimeType,
          mimeType === "image/png" ? undefined : quality
        );
      };

      img.onerror = reject;
      img.src = event.target.result;
    };

    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

export const renameFieldNames = (data, config) => {
  const contactKeyMap = config.contact.reduce((map, field) => {
    map[field.name] = field.variableName;
    return map;
  }, {});

  return {
    ...data,  // ← shallow copy to prevent mutation
    contact: Object.keys(data.contact).reduce((acc, key) => {
      const newKey = contactKeyMap[key] ?? key;
      acc[newKey] = data.contact[key];
      return acc;
    }, {})
  };
};


// Utility function to compute file checksum for JSON serialization
export const computeFileChecksum = async (file) => {
  try {
    const buffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
  } catch (error) {
    console.warn('Failed to compute checksum:', error);
    // Fallback: use file name + size as simple identifier
    return `${file.name}-${file.size}-${file.lastModified}`;
  }
};


export const deepEqual = (obj1, obj2) => {
  if (obj1 === obj2) return true;
  if (obj1 == null || obj2 == null) return false;
  if (typeof obj1 !== 'object' || typeof obj2 !== 'object') return false;

  // Handle File objects by comparing checksums
  if (obj1 instanceof File && obj2 instanceof File) {
    return obj1.toJSON() === obj2.toJSON();
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  for (let key of keys1) {
    if (!keys2.includes(key)) {
      return false;
    }
    if (!deepEqual(obj1[key], obj2[key])) {
      return false;
    }
  }

  return true;
};


// encode 
export function base64Encode(templateUniqueId) {
  return btoa(templateUniqueId)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}


// decode
export function base64Decode(templateUniqueId) {
  let base64 = templateUniqueId
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  while (base64.length % 4 !== 0) {
    base64 += '=';
  }
  return atob(base64);
}

function validateUrl(url) {
  const regex = new RegExp(
    /^(https|ftp):\/\/([A-Z0-9][A-Z0-9-]{0,61}[A-Z0-9]\.)+(?:[A-Z]{2,6}\.?|[A-Z0-9-]{2,}\.?)|\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}|(\[?[A-F0-9]*:[A-F0-9:]+\]?)\b(?::\d+)?(\/[A-Z0-9+&@#\/%=~_|$?!:,.]*)?$/i
  );

  if (!regex.test(url)) {
    return false;
  }

  try {
    const parsedUrl = new URL(url);

    if (!["http:", "https:", "ftp:"].includes(parsedUrl.protocol)) {
      return false;
    }

    const excludedHosts = ["localhost", "127.0.0.1", "::1"];
    if (
      excludedHosts.includes(parsedUrl.hostname) ||
      parsedUrl.hostname.startsWith("192.168.") ||
      parsedUrl.hostname.startsWith("10.")
    ) {
      return false;
    }

    const tld = parsedUrl.hostname.split(".").pop();
    if (tld.length < 2) {
      return false;
    }

    return true;
  } catch {
    return false;
  }
}

function s3SafeCharacters(value) {
  const regex = /^[0-9a-zA-Z _*'()&$-]+$/;
  return regex.test(value);
}

export function validateCampaignAndSaveTemplate(name, label) {
  if (!name) {
    return {
      error: true,
      message: `${label} name is required`
    }
  }
  if (name.length > 100) {
    return {
      error: true,
      message: `Maximum characters allowed is 100`
    }
  }
  if (!s3SafeCharacters(name)) {
    return {
      error: true,
      message: `${label} name is invalid. Allowed characters are A-Z, a-z, 0-9, -, _, $, &, (, ), *, '`
    }
  }
  return {
    error: false
  }
}

export function campaignInputValidation(campaign, url) {

  const validationResponse = validateCampaignAndSaveTemplate(campaign, "Campaign")

  if (validationResponse.error) {
    return validationResponse
  }

  if (!url) {
    return {
      error: true,
      message: "URL is required"
    }
  }
  if (!validateUrl(url)) {
    return {
      error: true,
      message: "Please enter a valid URL. Required format: https://yourcompany.com/landingPageX"
    }
  }
  return {
    error: false,
  }
}